import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Modal } from '../../../components/library';
import axios from '../../../utils/axios';
import { API_URL } from '../../../constants';
import { BikeIndexCheckStatus } from '../../../services/service/types';

interface InventoryBikeDetailsModalProps {
  open: boolean;
  handleClose: () => void;
  inspectionId: number;
  initialImageUrl?: string;
}

interface BikeIndexStatusResponse {
  inspectionId: number;
  sku: string;
  serialNumber: string | null;
  bikeIndexCheckStatus: BikeIndexCheckStatus | undefined;
  bikeIndexCheckedAt: string | null;
  bikeIndexSuspiciousIds: number[] | null;
  bikeIndexSuspiciousUrls: string[] | null;
  bikeIndexVerifiedByUserId: number | null;
  bikeIndexVerifiedAt: string | null;
  bikeIndexVerificationNotes: string | null;
  brand: string | null;
  model: string | null;
  wheelSize: string | null;
}

interface RecheckResponse {
  status: BikeIndexCheckStatus;
  checkedAt: string;
  suspiciousIds?: number[];
  suspiciousUrls?: string[];
}

interface VerifyResponse {
  bikeIndexCheckStatus: BikeIndexCheckStatus;
  bikeIndexVerifiedByUserId: number;
  bikeIndexVerifiedAt: string;
  bikeIndexVerificationNotes: string;
}

const statusChipStyle = (status: BikeIndexCheckStatus | undefined) => {
  switch (status) {
    case 'clean':
    case 'verified':
      return { backgroundColor: '#2e7d32', color: 'white' };
    case 'suspicious':
      return { backgroundColor: '#c62828', color: 'white' };
    case 'failed':
    case 'unchecked':
    default:
      return { backgroundColor: '#ed6c02', color: 'white' };
  }
};

const statusLabel = (status: BikeIndexCheckStatus | undefined) => {
  switch (status) {
    case 'clean':
      return 'BikeIndex: verified';
    case 'verified':
      return 'BikeIndex: manually verified';
    case 'suspicious':
      return 'BikeIndex: flagged — review';
    case 'failed':
      return 'BikeIndex: check failed — recheck';
    case 'unchecked':
    default:
      return 'BikeIndex: not checked';
  }
};

export const InventoryBikeDetailsModal: React.FC<InventoryBikeDetailsModalProps> = ({
  open,
  handleClose,
  inspectionId,
  initialImageUrl,
}) => {
  const [details, setDetails] = useState<BikeIndexStatusResponse | undefined>(undefined);
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [status, setStatus] = useState<BikeIndexCheckStatus | undefined>(undefined);
  const [suspiciousUrls, setSuspiciousUrls] = useState<string[]>([]);
  const [verifiedByUserId, setVerifiedByUserId] = useState<number | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [verificationNotes, setVerificationNotes] = useState<string | null>(null);
  const [verifyOpen, setVerifyOpen] = useState<boolean>(false);
  const [verifyNotesDraft, setVerifyNotesDraft] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!open || !inspectionId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        setMessage('');
        const res = await axios.get(
          `${API_URL}/service/inspections/${inspectionId}/bikeIndex/status`,
        );
        if (cancelled) return;
        const data = res?.data as BikeIndexStatusResponse | undefined;
        if (!data) {
          setError('Failed to load inspection.');
          return;
        }
        setDetails(data);
        setSerialNumber(data.serialNumber ?? '');
        setStatus(data.bikeIndexCheckStatus);
        setSuspiciousUrls(data.bikeIndexSuspiciousUrls ?? []);
        setVerifiedByUserId(data.bikeIndexVerifiedByUserId);
        setVerifiedAt(data.bikeIndexVerifiedAt);
        setVerificationNotes(data.bikeIndexVerificationNotes);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message ?? 'Failed to load inspection.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, inspectionId]);

  const readOnly = status === 'clean' || status === 'verified';

  const handleRecheck = async () => {
    if (!details?.inspectionId) return;
    try {
      setProcessing(true);
      setError('');
      setMessage('');

      // if the user has edited the serial, save it first so the server-side recheck
      // operates on the latest value (PUT also re-runs the BikeIndex check itself).
      if (serialNumber && details.serialNumber !== serialNumber) {
        await axios.put(`${API_URL}/service/inspections/${details.inspectionId}/serialNumber`, {
          serialNumber,
        });
      }

      const res = await axios.post(
        `${API_URL}/service/inspections/${details.inspectionId}/bikeIndex/recheck`,
      );
      const data = (res?.data ?? {}) as RecheckResponse;
      setStatus(data.status);
      setSuspiciousUrls(data.suspiciousUrls ?? []);
      // a fresh check clears any prior manual verification audit on the server;
      // keep client state in sync so the verified badge doesn't linger.
      setVerifiedByUserId(null);
      setVerifiedAt(null);
      setVerificationNotes(null);
      if (data.status === 'failed') {
        setMessage('BikeIndex is currently unavailable. Serial number was NOT verified.');
      } else if (data.status === 'suspicious') {
        setMessage('Serial flagged as suspicious. Review the BikeIndex hits below.');
      } else if (data.status === 'clean') {
        setMessage('BikeIndex check completed — serial verified.');
      } else {
        setMessage('BikeIndex check completed.');
      }
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Recheck failed.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitVerification = async () => {
    if (!details?.inspectionId) return;
    const notes = verifyNotesDraft.trim();
    if (!notes) {
      setError('Verification notes are required.');
      return;
    }
    try {
      setProcessing(true);
      setError('');
      setMessage('');
      const res = await axios.post(
        `${API_URL}/service/inspections/${details.inspectionId}/bikeIndex/verify`,
        { notes },
      );
      const data = (res?.data ?? {}) as VerifyResponse;
      setStatus(data.bikeIndexCheckStatus);
      setVerifiedByUserId(data.bikeIndexVerifiedByUserId);
      setVerifiedAt(data.bikeIndexVerifiedAt);
      setVerificationNotes(data.bikeIndexVerificationNotes);
      setVerifyOpen(false);
      setVerifyNotesDraft('');
      setMessage('Marked as manually verified.');
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Verification failed.');
    } finally {
      setProcessing(false);
    }
  };

  const brand = details?.brand;
  const model = details?.model;
  const wheelSize = details?.wheelSize;

  return (
    <Modal open={open} handleClose={handleClose} label="Bike Details" maxWidth={520}>
      <Box sx={{ py: 4, minWidth: 360 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Bike Details
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {initialImageUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  src={initialImageUrl}
                  alt={details?.sku || 'bike'}
                  style={{ maxHeight: 140, maxWidth: 200, objectFit: 'contain' }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {details?.sku && (
                <Typography sx={{ fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'grey.700', mr: 1 }}>
                    SKU
                  </Box>
                  {details.sku}
                </Typography>
              )}
              {brand && (
                <Typography sx={{ fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'grey.700', mr: 1 }}>
                    Brand
                  </Box>
                  {brand}
                </Typography>
              )}
              {model && (
                <Typography sx={{ fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'grey.700', mr: 1 }}>
                    Model
                  </Box>
                  {model}
                </Typography>
              )}
              {wheelSize && (
                <Typography sx={{ fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'grey.700', mr: 1 }}>
                    Wheel Size
                  </Box>
                  {wheelSize}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <TextField
              fullWidth
              size="small"
              label="Serial Number"
              variant="outlined"
              value={serialNumber}
              onChange={e => setSerialNumber(e.target.value)}
              disabled={readOnly || processing}
              sx={{ mb: 1 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip size="small" label={statusLabel(status)} sx={statusChipStyle(status)} />
              {details?.bikeIndexCheckedAt && (
                <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
                  Last checked: {new Date(details.bikeIndexCheckedAt).toLocaleString()}
                </Typography>
              )}
            </Box>

            {(status === 'suspicious' || status === 'verified') && suspiciousUrls.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'grey.700', mb: 0.5 }}>
                  BikeIndex potential matches (fuzzy):
                </Typography>
                {suspiciousUrls.map(url => (
                  <Box key={url}>
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: 13 }}
                    >
                      {url}
                    </Link>
                  </Box>
                ))}
              </Box>
            )}

            {status === 'verified' && verifiedAt && (
              <Box sx={{ mb: 2, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography sx={{ fontSize: 12, color: 'grey.700' }}>
                  Manually verified by user {verifiedByUserId ?? '—'} on{' '}
                  {new Date(verifiedAt).toLocaleString()}
                </Typography>
                {verificationNotes && (
                  <Typography
                    sx={{ fontSize: 12, color: 'grey.800', mt: 0.5, fontStyle: 'italic' }}
                  >
                    &quot;{verificationNotes}&quot;
                  </Typography>
                )}
              </Box>
            )}

            {status === 'suspicious' && verifyOpen && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Verification notes (required)"
                  placeholder="Describe why none of the BikeIndex matches are this bike."
                  value={verifyNotesDraft}
                  onChange={e => setVerifyNotesDraft(e.target.value)}
                  disabled={processing}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitVerification}
                    disabled={processing || !verifyNotesDraft.trim()}
                  >
                    Submit verification
                  </Button>
                  <Button
                    onClick={() => {
                      setVerifyOpen(false);
                      setVerifyNotesDraft('');
                    }}
                    color="inherit"
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {message && (
              <Typography sx={{ fontSize: 13, color: 'grey.700', mb: 1 }}>{message}</Typography>
            )}
            {error && (
              <Typography sx={{ fontSize: 13, color: 'error.main', mb: 1 }}>{error}</Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={handleClose} color="inherit">
                Close
              </Button>
              {status === 'suspicious' && !verifyOpen && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setVerifyOpen(true);
                    setError('');
                  }}
                  disabled={processing}
                >
                  Mark as verified
                </Button>
              )}
              {!readOnly && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRecheck}
                  disabled={processing || !serialNumber}
                >
                  {processing ? 'Checking…' : 'Recheck BikeIndex'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default InventoryBikeDetailsModal;
